import API_BASE from "../config.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useTextosSegmento from "../i18n/useTextosSegmento";
import CosmicIntro from "./CosmicIntro";
import InfoPopup from "./InfoPopup";

// Numero de suporte usado no link do WhatsApp para solicitar o adicional --
// mesmo numero hardcoded em pages/MeuPlano.js (WHATSAPP); aqui prefere o
// valor vindo de /config/sistema (editavel pelo admin) e cai neste como
// fallback se a config ainda nao carregou.
const WHATSAPP_PADRAO = "5511958325430";
function fmtPreco(v) { return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 }); }
function waLinkAdicionalChecklist(whatsapp, planoAtual, nomeAdicional, preco) {
  const msg = "Ola! Sou cliente do SAAS.GL.\n\nPlano atual: " + planoAtual + "\nGostaria de solicitar o adicional: " + nomeAdicional + "\nValor: +" + fmtPreco(preco) + "/mes\n\nPoderia me ajudar a liberar?";
  return "https://wa.me/" + (whatsapp || WHATSAPP_PADRAO) + "?text=" + encodeURIComponent(msg);
}

// Correcao pontual (18/08/2026) -- as rotas /checklist e /checklist/* nunca
// passavam pelo <Layout> (o modulo de checklist tem seu proprio shell/
// sidebar, ver checklist/components/AppShell.jsx), entao o overlay de
// "Sistema Bloqueado" por licenca expirada -- que só existe dentro do
// Layout -- nunca aparecia ali. Resultado: um tenant com licenca vencida
// ficava bloqueado no resto do painel mas continuava com acesso total ao
// checklist, contornando o bloqueio. Este componente reaplica a mesma
// checagem de licenca (mesmos endpoints/criterios do Layout) diretamente
// em volta do modulo de checklist, sem duplicar a sidebar/header do painel
// principal.
function getTenantIdFromToken() {
  try {
    const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token");
    if (!token) return (sessionStorage.getItem("tenant_id") || localStorage.getItem("tenant_id")) || "1";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return String(payload.tenant_id || (sessionStorage.getItem("tenant_id") || localStorage.getItem("tenant_id")) || "1");
  } catch {
    return (sessionStorage.getItem("tenant_id") || localStorage.getItem("tenant_id")) || "1";
  }
}
function getAuthHeaders() {
  const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token") || "";
  return token ? { Authorization: "Bearer " + token } : {};
}

// Plano/features liberadas -- mesmo padrao usado em OS.js/Agendamentos.js:
// le o cache local primeiro (evita flash de bloqueio indevido enquanto a
// rede responde) e revalida contra o backend em seguida.
function getPlanoAtual() {
  return ((sessionStorage.getItem("plano") || localStorage.getItem("plano")) || "basico").toLowerCase();
}
function getFeaturesCache(tenantId) {
  try { return JSON.parse(localStorage.getItem("dev_features_liberadas_tenant_" + tenantId) || "[]"); } catch { return []; }
}
// AUD-CK-L1: "lista vazia" e "ainda nao sei qual e a lista" sao coisas
// diferentes, e so a chave existir ou nao distingue as duas. Sem essa
// distincao, tirar o atalho do Premium criaria um defeito NOVO: oficina
// Premium sem cache, com a rede fora, ficaria bloqueada sem motivo.
function temCacheDeFeatures(tenantId) {
  try { return localStorage.getItem("dev_features_liberadas_tenant_" + tenantId) !== null; } catch { return false; }
}

export default function ChecklistLicenseGate({ children }) {
  const { t, i18n } = useTranslation();
  // AUD-F: t() sensivel ao segmento, para as chamadas de vocabulario
  // de veiculo desta tela. O resto segue no t() de sempre.
  const { tSeg } = useTextosSegmento();
  const navigate = useNavigate();
  const locale = ({ pt: "pt-BR", en: "en-US", es: "es-ES" })[i18n.language] || "pt-BR";
  const [licencaStatus, setLicencaStatus] = useState(null);
  const [sistemaConfig, setSistemaConfig] = useState({ email: "suporte@saas.gl", whatsapp: "" });
  const [introAtiva, setIntroAtiva] = useState(true);
  const tenantId = getTenantIdFromToken() || "1";
  const [plano, setPlano] = useState(getPlanoAtual());
  const [featuresLiberadas, setFeaturesLiberadas] = useState(() => getFeaturesCache(tenantId));
  // AUD-CK-L1: so vira true quando ha fonte confiavel -- cache gravado ou
  // resposta do backend.
  const [featuresConhecidas, setFeaturesConhecidas] = useState(() => temCacheDeFeatures(tenantId));
  // Simulacao de cobranca do adicional "checklist" -- vem inteira do
  // backend (POST /billing/adicionais/checklist/simular, ver
  // src/services/adicionais.py no tenant-service): valor proporcional
  // real (calculado pelos dias que faltam no ciclo atual da licenca, nao
  // um valor fixo no frontend), valor integral da proxima renovacao, e o
  // "modo" (proporcional / trial_gratuito / ja_incluso_premium). O
  // frontend NUNCA calcula ou decide esse valor sozinho -- so exibe o que
  // o backend mandou, pra nunca divergir do que de fato sera cobrado.
  const [simulacao, setSimulacao] = useState(null);
  const [ativando, setAtivando] = useState(false);
  const [erroAtivacao, setErroAtivacao] = useState("");

  useEffect(() => {
    fetch(API_BASE + "/api/v1/config/sistema")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.value) setSistemaConfig(d.value); })
      .catch(() => {});
    fetch(API_BASE + "/api/v1/config/licenca_tenant_" + tenantId, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.value && d.value.plano) setPlano(String(d.value.plano).toLowerCase());
        if (d && d.value && d.value.dataExpiracao) {
          const exp = new Date(d.value.dataExpiracao);
          const hoje = new Date();
          const diffMs = exp - hoje;
          const dias = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          const minutos = Math.ceil(diffMs / (1000 * 60));
          const horas = Math.ceil(diffMs / (1000 * 60 * 60));
          const expirado = diffMs <= 0;
          setLicencaStatus({ dias, minutos, horas, expirado });
        }
      })
      .catch(() => {});
    fetch(API_BASE + "/api/v1/config/features_liberadas_tenant_" + tenantId, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.value && Array.isArray(d.value.features)) {
          setFeaturesLiberadas(d.value.features);
          setFeaturesConhecidas(true);
          try { localStorage.setItem("dev_features_liberadas_tenant_" + tenantId, JSON.stringify(d.value.features)); } catch {}
        }
      })
      .catch(() => {});
    fetch(API_BASE + "/api/v1/billing/adicionais/checklist/simular", { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setSimulacao(d); })
      .catch(() => {});
  }, [tenantId]);

  // AUD-CK-L1 (15/10/2026) -- mesma regra do backend (services/license.py,
  // get_feature_status): a lista, QUANDO CONHECIDA, decide; o plano e so o
  // padrao de quem nunca foi configurado.
  //
  // Antes, o teste do plano ficava do lado esquerdo de um OU, antes da
  // lista -- e por isso desmarcar o checklist de uma oficina Premium no
  // admin nao desligava nada: o lado esquerdo ja resolvia a condicao
  // sozinho e a lista nunca era consultada.
  const temChecklist = featuresConhecidas
    ? featuresLiberadas.includes("checklist")
    : plano === "premium";

  // Contrata o adicional de verdade: backend decide se cobra (proporcional,
  // via Mercado Pago -- devolve checkout_url e o navegador e redirecionado
  // pra completar o pagamento) ou libera na hora sem custo (trial ainda nao
  // pago, ou plano ja incluso). Se der QUALQUER erro (Mercado Pago nao
  // configurado neste ambiente, rede fora, etc.) cai no fallback manual via
  // WhatsApp -- nunca deixa o cliente sem nenhum caminho pra liberar.
  async function ativarAdicional() {
    setAtivando(true);
    setErroAtivacao("");
    try {
      const r = await fetch(API_BASE + "/api/v1/billing/adicionais/checklist/ativar", {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErroAtivacao(d.detail || t("checklist.erro_ativar_adicional"));
        setAtivando(false);
        return;
      }
      if (d.checkout_url) {
        window.location.href = d.checkout_url;
        return;
      }
      // Liberado na hora (trial gratuito / ja incluso no premium / sem
      // custo residual) -- recarrega pra a proxima checagem de features ja
      // vir com o adicional liberado.
      window.location.reload();
    } catch {
      setErroAtivacao(t("checklist.erro_conexao_adicional"));
      setAtivando(false);
    }
  }

  if (licencaStatus && licencaStatus.expirado) {
    return (
      <div style={{ alignItems: "center", background: "#0f172a", bottom: 0, display: "flex", flexDirection: "column", justifyContent: "center", left: 0, position: "fixed", right: 0, top: 0, zIndex: 9999 }}>
        <div style={{ background: "#131929", border: "2px solid #ef4444", borderRadius: 16, maxWidth: 460, padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ color: "#ef4444", fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{t("layout.sistema_bloqueado")}</div>
          <div style={{ color: "#94a3b8", fontSize: 14, marginBottom: 24 }}>{t("layout.licenca_expirou_desc")}</div>
          <div style={{ color: "#64748b", fontSize: 12, marginBottom: 20 }}>
            {t("layout.suporte")}: {sistemaConfig.email || "suporte@saas.gl"}
            {sistemaConfig.whatsapp && (
              <a href={"https://wa.me/" + sistemaConfig.whatsapp} target="_blank" rel="noreferrer" style={{ color: "#22c55e", display: "block", marginTop: 4 }}>
                WhatsApp: {sistemaConfig.whatsapp}
              </a>
            )}
          </div>
          <button
            onClick={() => navigate("/meu-plano")}
            style={{ background: "#6366f1", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, padding: "10px 20px" }}
          >
            {t("layout.ir_meu_plano")}
          </button>
        </div>
      </div>
    );
  }

  // Recurso Premium nao liberado para esta oficina -- ao contrario do
  // bloqueio por licenca expirada, aqui a oficina esta em dia, so nao
  // contratou/nao teve este modulo liberado ainda. Por isso a tela e um
  // convite de upgrade (CTA para Meu Plano), nao um aviso de bloqueio por
  // inadimplencia. A checagem real que protege os dados fica no backend
  // (require_feature("checklist"), ver services/tenant-service); esta
  // tela e so a experiencia de quem esbarra na porta pela UI.
  if (!temChecklist) {
    // Dois caminhos de liberacao, nao so upgrade de plano: manter o plano
    // Basico e liberar so o Checklist como adicional (cobrado
    // PROPORCIONALMENTE aos dias que faltam pra proxima renovacao -- ver
    // simulacao acima), ou assinar o Premium (que ja inclui o Checklist
    // junto com o resto). O botao principal muda de acordo com o "modo" que
    // o backend devolveu; se a ativacao automatica falhar por qualquer
    // motivo, aparece um botao extra de fallback via WhatsApp.
    const temPreco = simulacao && simulacao.preco_adicional_mensal != null;
    const acoes = [];

    if (simulacao && !simulacao.ja_ativo) {
      if (simulacao.modo === "trial_gratuito") {
        acoes.push({ label: ativando ? t("checklist.processando") : t("checklist.liberar_gratis_trial"), variante: "primaria", onClick: ativarAdicional });
      } else if (simulacao.modo === "ja_incluso_premium") {
        acoes.push({ label: ativando ? t("checklist.processando") : t("checklist.liberar_agora"), variante: "primaria", onClick: ativarAdicional });
      } else if (simulacao.modo === "proporcional") {
        acoes.push({
          label: ativando ? t("checklist.processando") : t("checklist.pagar_e_liberar", { valor: fmtPreco(simulacao.valor_proporcional) }),
          variante: "primaria",
          onClick: ativarAdicional,
        });
      }
    }

    if (erroAtivacao) {
      acoes.push({
        label: t("checklist.liberar_via_whatsapp"),
        variante: acoes.length ? "secundaria" : "primaria",
        onClick: () => window.open(waLinkAdicionalChecklist(sistemaConfig.whatsapp, plano, "Checklist de Entrada de Veiculo", (simulacao && simulacao.preco_adicional_mensal) || 29.9), "_blank", "noopener,noreferrer"),
      });
    }

    acoes.push({ label: t("checklist.premium_upgrade"), variante: acoes.length ? "secundaria" : "primaria", onClick: () => navigate("/meu-plano") });
    acoes.push({ label: t("checklist.voltar_ao_painel"), variante: "fantasma", onClick: () => navigate("/") });

    return (
      <InfoPopup
        open
        onClose={() => navigate("/")}
        fecharAoClicarFora={false}
        icon="✨"
        eyebrow={t("checklist.premium_eyebrow")}
        title={t("checklist.premium_titulo")}
        description={t("checklist.premium_desc")}
        beneficios={[
          t("checklist.vantagem_1"),
          t("checklist.vantagem_2"),
          tSeg("checklist.vantagem_3"),
          t("checklist.vantagem_4"),
        ]}
        acoes={acoes}
      >
        {simulacao && !simulacao.ja_ativo && simulacao.modo === "proporcional" && (
          <div style={{ background: "#0f172a", border: "1px solid #1f2940", borderRadius: 12, margin: "0 0 18px", padding: "14px 16px", textAlign: "left" }}>
            <div style={{ alignItems: "center", color: "#94a3b8", display: "flex", fontSize: 12, justifyContent: "space-between", padding: "3px 0" }}>
              <span>{t("checklist.cobranca_hoje")} {simulacao.dias_restantes != null && "(" + t("checklist.dias_restantes", { count: simulacao.dias_restantes }) + ")"}</span>
              <strong style={{ color: "#f1f5f9" }}>{fmtPreco(simulacao.valor_proporcional)}</strong>
            </div>
            <div style={{ alignItems: "center", color: "#94a3b8", display: "flex", fontSize: 12, justifyContent: "space-between", padding: "3px 0" }}>
              <span>{t("checklist.a_partir_de", { data: simulacao.proxima_renovacao ? new Date(simulacao.proxima_renovacao).toLocaleDateString(locale) : "" })}</span>
              <strong style={{ color: "#f1f5f9" }}>{fmtPreco(simulacao.valor_integral_futuro)}/{t("meu_plano.mes")}</strong>
            </div>
          </div>
        )}
        {simulacao && !simulacao.ja_ativo && simulacao.modo === "trial_gratuito" && temPreco && (
          <div style={{ background: "#0f172a", border: "1px solid #1f2940", borderRadius: 12, margin: "0 0 18px", padding: "14px 16px", textAlign: "left" }}>
            <div style={{ color: "#94a3b8", fontSize: 12 }}>
              {t("checklist.gratis_durante_trial", { valor: fmtPreco(simulacao.valor_integral_futuro) })}
            </div>
          </div>
        )}
        {erroAtivacao && (
          <div style={{ color: "#f87171", fontSize: 12, marginBottom: 14, textAlign: "left" }}>{erroAtivacao}</div>
        )}
      </InfoPopup>
    );
  }

  return (
    <>
      {introAtiva && <CosmicIntro onFinish={() => setIntroAtiva(false)} />}
      {licencaStatus && !licencaStatus.expirado && licencaStatus.dias <= 7 && (
        <div style={{ background: licencaStatus.minutos <= 60 ? "#ef4444" : licencaStatus.horas <= 24 ? "#f97316" : "#f59e0b", color: "#fff", fontSize: 13, fontWeight: 600, padding: "10px 24px", position: "fixed", top: 0, left: 0, right: 0, zIndex: 9997, textAlign: "center" }}>
          ⚠️ {licencaStatus.minutos <= 60
            ? t("layout.expira_em_minutos", { count: licencaStatus.minutos })
            : licencaStatus.horas <= 24
              ? t("layout.expira_em_horas", { count: licencaStatus.horas })
              : t("layout.expira_em_dias", { count: licencaStatus.dias })}{" "}
          {t("layout.entre_em_contato_renovar")}
        </div>
      )}
      {children}
    </>
  );
}
